import { CustomListItemId, CustomListKey, formatDate } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import {
    QueryStatus,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import type { WorkflowRunDisplayState } from "components/workflow/workflow-run-status";
import {
    ResultDetail,
    RunStatusIcon,
    runStatusTextClassName,
} from "components/workflow/workflow-run-status";
import type { GetCustomListQuery } from "generated/graphql";
import React from "react";
import { useCustomList } from "store/selectors/bridge";
import {
    useCurrentRegion,
    useIsMultiRegionUser,
    useRegionId,
} from "store/selectors/hooks";
import { stableQueryOptionsRpc } from "util/requests";

type StatusMapping = inferRouterOutputs<AppRouter>["wellSkyImport"]["statusMapping"];

type StatusStageRow = StatusMapping["statuses"][number];

type MappedStage = StatusMapping["stages"][number];

type MappedStatusStageRow = StatusStageRow & { stageValue: string };

type LatestImport = inferRouterOutputs<AppRouter>["wellSkyImport"]["latest"];

type ImportRecord = NonNullable<LatestImport>;

type ImportResult = NonNullable<ImportRecord["result"]>;

type ImportCounts = ImportResult["referralSources"];

type StatusList = GetCustomListQuery["getCustomList"] | null;

type SavedMappingState = "missing" | "stale" | "usable";

const IMPORT_POLL_INTERVAL_MS = 3_000;

const cardClassName = "rounded-lg border border-gray-200";

const cardHeaderClassName =
    "flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5";

const cardTitleClassName = "text-sm font-semibold text-gray-900";

const cardFooterClassName =
    "flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-3 py-2.5";

const EMPTY_MAPPING: StatusMapping = { stages: [], statuses: [] };

const sameStages = (a: MappedStage[], b: MappedStage[]): boolean =>
    a.length === b.length &&
    a.every((stage, index) => stage.stageValue === b[index].stageValue);

const sameStatuses = (a: StatusStageRow[], b: StatusStageRow[]): boolean =>
    a.length === b.length &&
    a.every(
        (row, index) =>
            row.customListItemId === b[index].customListItemId &&
            row.stageValue === b[index].stageValue,
    );

const sameMapping = (a: StatusMapping, b: StatusMapping): boolean =>
    sameStages(a.stages, b.stages) && sameStatuses(a.statuses, b.statuses);

const isArchivedStatus = (statuses: StatusList, id: CustomListItemId): boolean =>
    statuses !== null && statuses.items.some(item => item.id === id && item.isArchived);

const isStaleRow = (row: StatusStageRow, statuses: StatusList): boolean =>
    isArchivedStatus(statuses, row.customListItemId);

const currentRowsOf = (mapping: StatusMapping, statuses: StatusList): StatusStageRow[] =>
    mapping.statuses.filter(row => !isStaleRow(row, statuses));

const hasStage = (row: StatusStageRow): row is MappedStatusStageRow =>
    row.stageValue !== null;

const isComplete = (mapping: StatusMapping, statuses: StatusList): boolean =>
    mapping.stages.length > 0 && currentRowsOf(mapping, statuses).every(hasStage);

const unmappedCountOf = (mapping: StatusMapping, statuses: StatusList): number =>
    currentRowsOf(mapping, statuses).filter(row => !hasStage(row)).length;

const unmappedHintOf = (count: number): string =>
    count === 1
        ? "1 status still needs a WellSky stage before the mapping can be saved."
        : `${count} statuses still need a WellSky stage before the mapping can be saved.`;

const unmappedSavedHintOf = (count: number): string =>
    count === 1
        ? "1 status has no WellSky stage yet. Choose a stage for it and save the mapping."
        : `${count} statuses have no WellSky stage yet. Choose a stage for each and save the mapping.`;

const staleStatusNamesOf = (mapping: StatusMapping, statuses: StatusList): string[] =>
    mapping.statuses.filter(row => isStaleRow(row, statuses)).map(row => row.statusName);

const staleHintOf = (statusNames: string[]): string => {
    const quoted = statusNames.map(name => `"${name}"`).join(", ");
    return statusNames.length === 1
        ? `Status ${quoted} has been archived or deleted. It is left out of the mapping when you save it.`
        : `Statuses ${quoted} have been archived or deleted. They are left out of the mapping when you save it.`;
};

const mappingInputOf = (mapping: StatusMapping, statuses: StatusList) => ({
    stages: mapping.stages,
    statuses: currentRowsOf(mapping, statuses)
        .filter(hasStage)
        .map(row => ({
            customListItemId: row.customListItemId,
            stageValue: row.stageValue,
        })),
});

const savedMappingStateOf = (
    saved: StatusMapping,
    statuses: StatusList,
): SavedMappingState => {
    if (saved.stages.length === 0) {
        return "missing";
    }
    return staleStatusNamesOf(saved, statuses).length > 0 ? "stale" : "usable";
};

const isImportActive = (latest: LatestImport | undefined): boolean =>
    latest?.status === "pending" || latest?.status === "running";

const pollIntervalOf = (
    status: QueryStatus,
    latest: LatestImport | undefined,
): number | false =>
    status !== "error" && isImportActive(latest) ? IMPORT_POLL_INTERVAL_MS : false;

type ImportReadiness =
    | { kind: "mappingMissing" }
    | { kind: "mappingStale" }
    | { kind: "checking" }
    | { kind: "importActive" }
    | { kind: "regionMissing" }
    | { kind: "ready" };

interface ImportCardState {
    mapping: SavedMappingState;
    latestLoading: boolean;
    latest: LatestImport;
    regionSelected: boolean;
}

const importReadinessOf = (state: ImportCardState): ImportReadiness => {
    if (state.mapping === "missing") {
        return { kind: "mappingMissing" };
    }
    if (state.mapping === "stale") {
        return { kind: "mappingStale" };
    }
    if (state.latestLoading) {
        return { kind: "checking" };
    }
    if (isImportActive(state.latest)) {
        return { kind: "importActive" };
    }
    if (!state.regionSelected) {
        return { kind: "regionMissing" };
    }
    return { kind: "ready" };
};

const importBlockerOf = (readiness: ImportReadiness): string | null => {
    switch (readiness.kind) {
        case "mappingMissing":
            return "Save the status mapping before importing.";
        case "mappingStale":
            return "A status in the mapping has been archived or deleted. Save the status mapping again before importing.";
        case "regionMissing":
            return "Select an office to import into.";
        case "checking":
        case "importActive":
        case "ready":
            return null;
        default: {
            const exhaustiveCheck: never = readiness;
            throw new Error(`Unhandled readiness: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

const prospectCountsLineOf = (counts: ImportCounts): string =>
    `Prospects: ${counts.created} created, ${counts.linked} linked, ${counts.skipped} skipped.`;

const referralSourceCountsLineOf = (counts: ImportCounts): string =>
    `Referral sources: ${counts.created} created, ${counts.linked} linked, ${counts.skipped} skipped as deactivated in WellSky.`;

const countsLinesOf = (result: ImportResult): string[] => [
    referralSourceCountsLineOf(result.referralSources),
    prospectCountsLineOf(result.prospects),
];

export const WellSkyImportSection: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const mappingQuery = useQuery({
        ...trpcClient.wellSkyImport.statusMapping.queryOptions(),
        ...stableQueryOptionsRpc(),
        refetchOnMount: "always",
    });
    const { customList: statuses } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const [editedMapping, setEditedMapping] = React.useState<StatusMapping | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const clearMessages = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
    }, []);

    const proposeMutation = useMutation({
        ...trpcClient.wellSkyImport.proposeStatusMapping.mutationOptions(),
        onSuccess: result => {
            clearMessages();
            setEditedMapping(result);
        },
        onError: error =>
            setErrorMessage(error.message || "Could not generate a mapping"),
    });

    const saveMutation = useMutation({
        ...trpcClient.wellSkyImport.saveStatusMapping.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: trpcClient.wellSkyImport.statusMapping.queryKey(),
            });
            setErrorMessage(null);
            setEditedMapping(null);
            setSuccessMessage("Status mapping saved");
        },
        onError: error => setErrorMessage(error.message || "Could not save the mapping"),
    });

    const savedMapping: StatusMapping = mappingQuery.data ?? EMPTY_MAPPING;
    const mapping = editedMapping ?? savedMapping;
    const mappingGenerated = mapping.stages.length > 0;
    const unmapped = mappingGenerated ? unmappedCountOf(mapping, statuses) : 0;
    const staleStatuses = staleStatusNamesOf(mapping, statuses);
    const edited = editedMapping !== null;
    const needsResave = staleStatuses.length > 0;
    const canSave =
        isComplete(mapping, statuses) &&
        (!sameMapping(mapping, savedMapping) || needsResave) &&
        !saveMutation.isPending;

    const handleChange = React.useCallback(
        (customListItemId: CustomListItemId, stageValue: string | null) => {
            clearMessages();
            setEditedMapping(current => {
                const base = current ?? savedMapping;
                return {
                    ...base,
                    statuses: base.statuses.map(row =>
                        row.customListItemId === customListItemId
                            ? { ...row, stageValue }
                            : row,
                    ),
                };
            });
        },
        [clearMessages, savedMapping],
    );

    const handleCreate = React.useCallback(() => {
        clearMessages();
        proposeMutation.mutate();
    }, [clearMessages, proposeMutation]);

    const handleRegenerate = React.useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Re-generate the status mapping?",
            "This resets the status mapping from your WellSky stages and overwrites any changes you have made.",
            "Re-generate mapping",
            "Cancel",
            { confirmStyle: "alert" },
        );
        if (confirmed) {
            clearMessages();
            proposeMutation.mutate();
        }
    }, [clearMessages, proposeMutation]);

    const handleSave = React.useCallback(() => {
        clearMessages();
        saveMutation.mutate(mappingInputOf(mapping, statuses));
    }, [clearMessages, mapping, saveMutation, statuses]);

    const handleCancel = React.useCallback(() => {
        clearMessages();
        setEditedMapping(null);
    }, [clearMessages]);

    return (
        <div className="space-y-4">
            <section className={cardClassName}>
                <div className={cardHeaderClassName}>
                    <h3 className={cardTitleClassName}>Status mapping</h3>
                </div>
                <p className="px-3 pt-3 text-sm text-gray-500">
                    A client sent to WellSky moves to the stage mapped to its status
                    whenever that status changes, and imported prospects take the status
                    mapped to their stage.
                </p>
                {successMessage ? (
                    <div className="px-3 pt-3">
                        <InlineBanner type="success">{successMessage}</InlineBanner>
                    </div>
                ) : null}
                {errorMessage ? (
                    <div className="px-3 pt-3">
                        <InlineBanner type="error">{errorMessage}</InlineBanner>
                    </div>
                ) : null}
                {unmapped > 0 ? (
                    <div className="px-3 pt-3">
                        <InlineBanner type="warning">
                            {edited
                                ? unmappedHintOf(unmapped)
                                : unmappedSavedHintOf(unmapped)}
                        </InlineBanner>
                    </div>
                ) : null}
                {staleStatuses.length > 0 ? (
                    <div className="px-3 pt-3">
                        <InlineBanner type="warning">
                            {staleHintOf(staleStatuses)}
                        </InlineBanner>
                    </div>
                ) : null}
                <StatusMappingCardBody
                    {...statusMappingBodyOf(mappingQuery, mapping, handleChange)}
                />
                <div className={cardFooterClassName}>
                    <SecondaryButton
                        type="button"
                        onClick={mappingGenerated ? handleRegenerate : handleCreate}
                        disabled={proposeMutation.isPending || mappingQuery.isLoading}>
                        {proposeMutation.isPending ? <Spinner /> : null}
                        <span className={proposeMutation.isPending ? "ml-2" : ""}>
                            {mappingGenerated ? "Re-generate mapping" : "Create mapping"}
                        </span>
                    </SecondaryButton>
                    {edited || needsResave ? (
                        <div className="flex items-center space-x-2">
                            {edited ? (
                                <SecondaryButton type="button" onClick={handleCancel}>
                                    Cancel
                                </SecondaryButton>
                            ) : null}
                            <PrimaryButton
                                type="button"
                                onClick={handleSave}
                                disabled={!canSave}>
                                {saveMutation.isPending ? <Spinner /> : null}
                                <span className={saveMutation.isPending ? "ml-2" : ""}>
                                    Save
                                </span>
                            </PrimaryButton>
                        </div>
                    ) : null}
                </div>
            </section>
            <WellSkyImportCard mapping={savedMappingStateOf(savedMapping, statuses)} />
        </div>
    );
};

type StatusMappingChangeHandler = (
    customListItemId: CustomListItemId,
    stageValue: string | null,
) => void;

type StatusMappingCardBodyProps =
    | { kind: "loading" }
    | { kind: "failed"; message: string }
    | { kind: "loaded"; mapping: StatusMapping; onChange: StatusMappingChangeHandler };

interface StatusMappingQueryState {
    isLoading: boolean;
    error: { message: string } | null;
}

const statusMappingBodyOf = (
    query: StatusMappingQueryState,
    mapping: StatusMapping,
    onChange: StatusMappingChangeHandler,
): StatusMappingCardBodyProps => {
    if (query.isLoading) {
        return { kind: "loading" };
    }
    if (query.error) {
        return { kind: "failed", message: query.error.message };
    }
    return { kind: "loaded", mapping, onChange };
};

const StatusMappingCardBody: React.FC<StatusMappingCardBodyProps> = props => {
    switch (props.kind) {
        case "loading":
            return (
                <div className="px-3 py-3">
                    <Spinner />
                </div>
            );
        case "failed":
            return (
                <div className="px-3 py-3">
                    <InlineBanner type="error">{props.message}</InlineBanner>
                </div>
            );
        case "loaded":
            return (
                <StatusMappingRows mapping={props.mapping} onChange={props.onChange} />
            );
        default: {
            const exhaustiveCheck: never = props;
            throw new Error(`Unhandled mapping body: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

interface StatusMappingRowsProps {
    mapping: StatusMapping;
    onChange: StatusMappingChangeHandler;
}

const StatusMappingRows: React.FC<StatusMappingRowsProps> = props => {
    const { mapping, onChange } = props;
    if (mapping.stages.length === 0) {
        return (
            <div className="px-3 py-3 text-sm text-gray-500">
                No statuses are mapped yet. Create a mapping from your WellSky stages,
                then adjust it before saving.
            </div>
        );
    }
    return (
        <div className="divide-y divide-gray-200">
            {mapping.statuses.map(row => (
                <StatusMappingRowEditor
                    key={row.customListItemId}
                    row={row}
                    stages={mapping.stages}
                    onChange={onChange}
                />
            ))}
        </div>
    );
};

interface StatusMappingRowEditorProps {
    row: StatusStageRow;
    stages: MappedStage[];
    onChange: StatusMappingChangeHandler;
}

const StatusMappingRowEditor: React.FC<StatusMappingRowEditorProps> = props => {
    const { row, stages, onChange } = props;
    const { customList: statuses } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const stale = isStaleRow(row, statuses);
    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) =>
            onChange(row.customListItemId, event.target.value || null),
        [onChange, row.customListItemId],
    );
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <span className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-900">
                {row.statusName}
                {stale ? (
                    <Badge type="warning" value="Archived or deleted status" />
                ) : null}
            </span>
            <div className="w-full sm:w-64">
                <Select
                    value={row.stageValue ?? ""}
                    onChange={handleChange}
                    disabled={stale}
                    aria-label={row.statusName}>
                    <option value="">Choose a stage</option>
                    {stages.map(stage => (
                        <option key={stage.stageValue} value={stage.stageValue}>
                            {stage.stageName}
                        </option>
                    ))}
                </Select>
            </div>
        </div>
    );
};

interface WellSkyImportCardProps {
    mapping: SavedMappingState;
}

const WellSkyImportCard: React.FC<WellSkyImportCardProps> = props => {
    const { mapping } = props;
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const regionId = useRegionId();
    const region = useCurrentRegion();
    const isMultiRegion = useIsMultiRegionUser();
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const latestQuery = useQuery({
        ...trpcClient.wellSkyImport.latest.queryOptions(),
        refetchInterval: current =>
            pollIntervalOf(current.state.status, current.state.data),
    });

    const startMutation = useMutation({
        ...trpcClient.wellSkyImport.start.mutationOptions(),
        onSuccess: async () => {
            setErrorMessage(null);
            await queryClient.invalidateQueries({
                queryKey: trpcClient.wellSkyImport.latest.queryKey(),
            });
        },
        onError: error => setErrorMessage(error.message || "Could not start the import"),
    });

    const latest = latestQuery.data ?? null;
    const readiness = importReadinessOf({
        mapping,
        latestLoading: latestQuery.isPending,
        latest,
        regionSelected: !!regionId,
    });
    const blocker = importBlockerOf(readiness);
    const canImport =
        readiness.kind === "ready" && !!regionId && !startMutation.isPending;
    const regionNote =
        isMultiRegion && region
            ? ` Records are created in the ${region.name} office.`
            : "";

    const handleImport = React.useCallback(async () => {
        if (!regionId) {
            return;
        }
        const confirmed = await fancyConfirm(
            "Import from WellSky?",
            "The import runs in the background and can take a while. Existing organizations, communities, contacts and home care clients with the same name are linked rather than duplicated, and only the details they are missing are filled in from WellSky. Prospects already imported or sent to WellSky are skipped; a prospect with no match, or whose match is ambiguous, becomes a new home care client.",
            "Yes, import",
            "Cancel",
            { confirmStyle: "question" },
        );
        if (confirmed) {
            setErrorMessage(null);
            startMutation.mutate({ regionId });
        }
    }, [regionId, startMutation]);

    return (
        <section className={cardClassName}>
            <div className={cardHeaderClassName}>
                <h3 className={cardTitleClassName}>Import from WellSky</h3>
            </div>
            <div className="space-y-3 px-3 py-3">
                <p className="text-sm text-gray-500">
                    Imports the account&apos;s active referral sources and prospects as
                    organizations, contacts and home care clients, linked for live sync.
                    {regionNote}
                </p>
                {blocker ? <InlineBanner type="warning">{blocker}</InlineBanner> : null}
                {latestQuery.isLoading ? <Spinner /> : null}
                {latestQuery.error ? (
                    <InlineBanner type="error">{latestQuery.error.message}</InlineBanner>
                ) : null}
                {latest ? <LatestImportPanel latest={latest} /> : null}
                {errorMessage ? (
                    <InlineBanner type="error">{errorMessage}</InlineBanner>
                ) : null}
            </div>
            <div className={cardFooterClassName}>
                <div className="ml-auto">
                    <PrimaryButton
                        type="button"
                        onClick={handleImport}
                        disabled={!canImport}>
                        {startMutation.isPending ? <Spinner /> : null}
                        <span className={startMutation.isPending ? "ml-2" : ""}>
                            Import
                        </span>
                    </PrimaryButton>
                </div>
            </div>
        </section>
    );
};

interface LatestImportPanelProps {
    latest: ImportRecord;
}

const LatestImportPanel: React.FC<LatestImportPanelProps> = props => {
    const { latest } = props;
    switch (latest.status) {
        case "pending":
        case "running":
            return (
                <div className="flex items-center space-x-2">
                    <Spinner />
                    <span className="text-sm text-gray-700">
                        {latest.progressMessage || "Waiting to start"}
                    </span>
                </div>
            );
        case "failed":
            return (
                <ImportOutcome
                    state="error"
                    title="Import failed"
                    finishedAt={latest.finishedAt}
                    details={[latest.errorMessage || "The import did not finish."]}
                />
            );
        case "succeeded":
            return <ImportResultPanel latest={latest} />;
        default: {
            const exhaustiveCheck: never = latest.status;
            throw new Error(
                `Unhandled import status: ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

interface ImportOutcomeProps {
    state: WorkflowRunDisplayState;
    title: string;
    finishedAt: string | null;
    details: string[];
}

const ImportOutcome: React.FC<ImportOutcomeProps> = props => {
    const { state, title, finishedAt, details } = props;
    return (
        <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                    <RunStatusIcon state={state} />
                    <span>{title}</span>
                </span>
                <span className={runStatusTextClassName}>{formatDate(finishedAt)}</span>
            </div>
            {details.map(detail => (
                <ResultDetail key={detail} detail={detail} className="pl-7" />
            ))}
        </div>
    );
};

const ImportResultPanel: React.FC<LatestImportPanelProps> = props => {
    const { latest } = props;
    const result = latest.result;
    if (!result) {
        return null;
    }
    return (
        <div className="space-y-3">
            <ImportOutcome
                state="success"
                title="Import finished"
                finishedAt={latest.finishedAt}
                details={countsLinesOf(result)}
            />
            {result.deactivatedReferralSourcesImported.length > 0 ? (
                <ImportNameList
                    title="Deactivated referral sources imported"
                    description="Deactivated in WellSky, but an imported prospect still uses them."
                    items={result.deactivatedReferralSourcesImported}
                />
            ) : null}
        </div>
    );
};

interface ImportNameListProps {
    title: string;
    description: string | null;
    items: string[];
}

const ImportNameList: React.FC<ImportNameListProps> = props => {
    const { title, description, items } = props;
    return (
        <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
            {description ? (
                <div className="text-xs text-gray-500">{description}</div>
            ) : null}
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                {items.map((item, index) => (
                    <li key={`${index}-${item}`}>{item}</li>
                ))}
            </ul>
        </div>
    );
};
